import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { Activity } from 'lib/_types'
import { limitNumber } from 'lib/helpers'
import { sanitizeContent } from '../helpers'

import { ActivityPostPreview } from './ActivityPostPreview'
import { ActivitySendLetterBackButton } from './ActivitySendLetterBackButton'

import './style.scss'

type Props = {
  activity: Activity
}

export const ActivityText = ({ activity }: Props) => {
  const { t, i18n } = useTranslation()

  const nickname = activity.user?.nickname
  const topic = 'group' in activity ? activity.group?.topic : ''

  switch (activity.type) {
    case 'like': {
      const likersCount =
        activity.to_post?.likers_count ?? activity.to_post?.likers?.length ?? 0

      return (
        <>
          <Text>
            {likersCount > 1
              ? t('activity_list.like.more_than_1', {
                  nickname,
                  likers: likersCount - 1,
                })
              : t('activity_list.like.1', { nickname })}
          </Text>

          <ActivityPostPreview
            postType={activity.to_post?.post_type}
            text={activity.to_post?.text}
            color="gray"
          />
        </>
      )
    }

    case 'follow': {
      const followersCount =
        activity.followers_count ?? activity.followers?.length ?? 0

      return (
        <Text>
          {followersCount > 1
            ? t('activity_list.followers.more_than_1', {
                nickname,
                followers: followersCount - 1,
              })
            : t('activity_list.followers.1', { nickname })}
        </Text>
      )
    }

    case 'follow_request':
      return <Text>{t('activity_list.follow_request', { nickname })}</Text>

    case 'follow_accepted':
      return <Text>{t('activity_list.follow_accepted', { nickname })}</Text>

    case 'group_promote':
      return (
        <Text>{t('activity_list.group_promote', { nickname, topic })}</Text>
      )

    case 'group_ownership':
      return <Text>{t('activity_list.group_ownership', { topic })}</Text>

    case 'group_accepted':
      return <Text>{t('activity_list.group_accepted', { topic })}</Text>

    case 'group_request':
      return <Text>{t('activity_list.group_request', { topic })}</Text>

    case 'group_created':
      return (
        <Text>{t('activity_list.group_created', { nickname, topic })}</Text>
      )

    case 'group_walkthrough_request':
      return (
        <Text>
          {t('activity_list.group_walkthrough_request', { nickname, topic })}
        </Text>
      )

    case 'profile_screenshot':
      return <Text>{t('activity_list.profile_screenshot', { nickname })}</Text>

    case 'reply':
      return (
        <div>
          <Text>
            {activity.group
              ? activity.from_post?.thread_id
                ? t('activity_list.thread_reply', { nickname })
                : t('activity_list.reply_group', { nickname })
              : t('activity_list.reply', { nickname })}
          </Text>
          <ActivityPostPreview
            postType={activity.from_post?.post_type}
            text={activity.from_post?.text}
          />
          <ActivityPostPreview
            postType={activity.to_post?.post_type}
            text={activity.to_post?.text}
            color="gray"
          />
        </div>
      )

    case 'repost':
      return (
        <div>
          <Text>
            {activity.group
              ? t('activity_list.repost_group', { nickname })
              : t('activity_list.repost', { nickname })}
          </Text>

          <ActivityPostPreview
            postType={activity.from_post?.post_type}
            text={activity.from_post?.text}
          />
          <ActivityPostPreview
            postType={activity.to_post?.post_type}
            text={activity.to_post?.text}
            color="gray"
          />
        </div>
      )

    case 'group_invite':
      return <Text>{t('activity_list.group_invite', { nickname, topic })}</Text>

    case 'review_pending':
      return (
        <>
          <Text>{t('activity_list.review_pending', { nickname })}</Text>
          <ActivityPostPreview text={activity.metadata?.content_preview} />
        </>
      )

    case 'review_gift':
      return (
        <>
          <Text>{t('activity_list.review_gift', { nickname })}</Text>
          <ActivityPostPreview text={activity.metadata?.content_preview} />
        </>
      )

    case 'conference_call_invite': {
      if (activity.metadata?.bulk_invitation) {
        return (
          <Text>
            {activity.metadata?.bulk_invitation
              ? t('activity_list.conference_call_invite_bulk', { nickname })
              : t('activity_list.conference_call_invite', { nickname })}
          </Text>
        )
      }
    }

    case 'id_check_checking':
      return <Text>{t('activity_list.id_check_checking')}</Text>

    case 'id_check_success':
      return <Text>{t('activity_list.id_check_success')}</Text>

    case 'id_check_failed':
      return <Text>{t('activity_list.id_check_failed')}</Text>

    case 'today_news':
    case 'popular_post':
      return (
        <div>
          <Text>{t('activity_list.popular_post', { nickname })}</Text>
          <ActivityPostPreview
            postType={activity.from_post?.post_type}
            text={activity.from_post?.text}
            color="gray"
          />
        </div>
      )

    case 'vip_game_reward':
      return (
        <Text>
          {t('activity_list.vip_game_reward', { total: activity.vip_reward })}
        </Text>
      )

    case 'user_birth_date':
      const userCount =
        activity.birthday_users_count ?? activity.birthday_users?.length ?? 0

      const date = dayjs((activity.created_at || 0) * 1000).format(
        i18n.language === 'ja' ? 'M/D' : 'MMMM D'
      )

      return (
        <Text>
          {userCount > 1
            ? t('activity_list.user_birth_date.more_than_1', {
                date,
                nickname,
                users: limitNumber(userCount - 1, 1000),
              })
            : t('activity_list.user_birth_date.1', {
                date,
                nickname,
              })}
        </Text>
      )

    case 'group_transfer_request':
      return (
        <Text>
          {t('activity_list.group_transfer_request', { nickname, topic })}
        </Text>
      )

    case 'group_transfer_accepted':
      return (
        <Text>
          {t('activity_list.group_transfer_accepted', { nickname, topic })}
        </Text>
      )

    case 'group_transfer_declined':
      return (
        <Text>
          {t('activity_list.group_transfer_declined', { nickname, topic })}
        </Text>
      )

    case 'group_deputization_request':
      return (
        <Text>
          {t('activity_list.group_deputization_request', { nickname, topic })}
        </Text>
      )

    case 'group_deputization_accepted':
      return (
        <Text>
          {t('activity_list.group_deputization_accepted', { nickname, topic })}
        </Text>
      )

    case 'group_deputization_declined':
      return (
        <Text>
          {t('activity_list.group_deputization_declined', { nickname, topic })}
        </Text>
      )

    case 'post_group_tagged_all':
      return (
        <>
          <Text>
            {t('activity_list.post_group_tagged_all', { nickname, topic })}
          </Text>
          <ActivityPostPreview
            postType={activity.from_post?.post_type}
            text={activity.from_post?.text}
          />
        </>
      )

    case 'post_move_to_thread':
      return (
        <>
          <Text>{t('activity_list.post_move_to_thread', { nickname })}</Text>
          <ActivityPostPreview
            postType={activity.from_post?.post_type}
            text={activity.from_post?.text}
            color="gray"
          />
        </>
      )

    case 'tagged':
      return (
        <>
          <Text>{t('activity_list.tagged', { nickname })}</Text>
          <ActivityPostPreview
            postType={activity.from_post?.post_type}
            text={activity.from_post?.text}
          />
        </>
      )

    case 'custom_announcement':
      return (
        <>
          <Text>
            {activity.metadata?.title || t('activity_list.custom_announcement')}
          </Text>
          <ActivityPostPreview text={activity.metadata?.body} color="gray" />
        </>
      )

    case 'wallet_daily_quests_completed':
      return <Text>{t('activity_list.wallet_daily_quests_completed')}</Text>

    case 'invitee_create_account':
      return (
        <Text>{t('activity_list.invitee_create_account', { nickname })}</Text>
      )

    case 'follow_inviter':
      return <Text>{t('activity_list.follow_inviter', { nickname })}</Text>

    case 'followed_inviter':
      return <Text>{t('activity_list.followed_inviter', { nickname })}</Text>

    case 'gift_received_user':
      return activity.metadata?.anonymous ? (
        <Text>{t('activity_list.gift_received_anonymous')}</Text>
      ) : (
        <>
          <Text>{t('activity_list.gift_received_user', { nickname })}</Text>
          <ActivitySendLetterBackButton id={activity.user?.id} />
        </>
      )

    case 'gift_received_post':
      return activity.metadata?.anonymous ? (
        <Text>{t('activity_list.gift_received_anonymous')}</Text>
      ) : (
        <>
          <Text>{t('activity_list.gift_received_post', { nickname })}</Text>
          <ActivitySendLetterBackButton id={activity.user?.id} />
        </>
      )

    case 'gift_received_group':
      return activity.metadata?.anonymous ? (
        <Text>{t('activity_list.gift_received_anonymous')}</Text>
      ) : (
        <>
          <Text>
            {t('activity_list.gift_received_group', {
              nickname,
              topic: activity.metadata?.group_topic,
            })}
          </Text>
          <ActivitySendLetterBackButton id={activity.user?.id} />
        </>
      )

    case 'gift_received_group_post':
      return activity.metadata?.anonymous ? (
        <Text>{t('activity_list.gift_received_anonymous')}</Text>
      ) : (
        <>
          <Text>
            {t('activity_list.gift_received_group_post', { nickname })}
          </Text>
          <ActivitySendLetterBackButton id={activity.user?.id} />
        </>
      )

    default:
      // prevent typescript error: Property 'type' does not exist on type 'never'.
      return <>{activity['type']}</>
  }
}

const Text = ({ children }: { children: string }) => (
  <span
    dangerouslySetInnerHTML={{
      __html: sanitizeContent(children),
    }}
  />
)
